import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { apiRequest } from "../lib/queryClient";
import {
  EXPERIMENT_POST_SURVEY,
  buildSurveyResponsePayload,
  countMissingRequiredSurveyAnswers,
  createEmptySurveyAnswers,
  isCurrentSurveyResponse,
  normalizeSurveyAnswers,
  type ExperimentSurveyAnswers,
  type ExperimentSurveyDefinition,
  type ExperimentSurveyTextItem,
} from "../lib/experiment-survey";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";
import { toUserFacingError } from "../lib/user-facing-error";
import { EXPERIMENT_PRIMARY_ACTION_CLASS } from "../lib/experiment-ui";

interface SavedSurveyResponse {
  response: {
    id?: number;
    response_json?: unknown;
    updated?: string;
  } | null;
}

interface ExperimentPostSurveyFormProps {
  interviewId: number;
  participantCode?: string | null;
  definition?: ExperimentSurveyDefinition;
  embedded?: boolean;
  onSubmitted?: () => void;
}

export function ExperimentPostSurveyForm({
  interviewId,
  participantCode,
  definition = EXPERIMENT_POST_SURVEY,
  embedded = false,
  onSubmitted,
}: ExperimentPostSurveyFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ["experiment-post-survey", interviewId, definition.id];
  const [answers, setAnswers] = useState<ExperimentSurveyAnswers>(() =>
    createEmptySurveyAnswers(definition),
  );
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const savedResponseQuery = useQuery<SavedSurveyResponse>({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        form_type: "post_survey",
        interview_id: String(interviewId),
      });
      const response = await fetch(`/api/experiment/form-responses?${params}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error(await response.text());
      return response.json();
    },
    staleTime: 0,
  });

  useEffect(() => {
    const saved = savedResponseQuery.data?.response;
    if (!saved) return;
    setAnswers(normalizeSurveyAnswers(saved.response_json, definition));
    setSavedAt(isCurrentSurveyResponse(saved.response_json, definition) ? saved.updated ?? null : null);
  }, [definition, savedResponseQuery.data?.response]);

  const missingCount = useMemo(
    () => countMissingRequiredSurveyAnswers(answers, definition),
    [answers, definition],
  );

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (missingCount > 0) throw new Error("必須の質問にすべて回答してください");
      const submittedAt = new Date().toISOString();
      await apiRequest("POST", "/api/experiment/form-responses", {
        formType: "post_survey",
        interviewId,
        response: buildSurveyResponsePayload(answers, submittedAt, definition),
      });
      return submittedAt;
    },
    onSuccess: async (submittedAt) => {
      setSavedAt(submittedAt);
      await queryClient.invalidateQueries({ queryKey });
      toast({ title: "アンケートを保存しました" });
      onSubmitted?.();
    },
    onError: (error: Error) => {
      const userFacingError = toUserFacingError(error, "survey");
      toast({
        ...userFacingError,
        variant: "destructive",
      });
    },
  });

  const scaleValues = Array.from(
    { length: definition.scale.max - definition.scale.min + 1 },
    (_, index) => definition.scale.min + index,
  );

  return (
    <section
      aria-labelledby={`survey-title-${interviewId}`}
      className={embedded ? "border border-zinc-200 bg-white" : "bg-white"}
      style={{ borderRadius: embedded ? 8 : 0 }}
    >
      <div className="border-b border-zinc-200 px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-bold text-cyan-700">学生振り返りアンケート</p>
            <h2 id={`survey-title-${interviewId}`} className="mt-1 text-xl font-bold text-zinc-950">
              {definition.title}
            </h2>
            <p className="mt-2 text-sm leading-6 text-zinc-600">{definition.subtitle}</p>
          </div>
          <dl className="grid shrink-0 grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-zinc-600">
            {participantCode && (
              <>
                <dt className="font-bold">学籍番号</dt>
                <dd className="font-mono">{participantCode}</dd>
              </>
            )}
            <dt className="font-bold">面接ID</dt>
            <dd className="font-mono">{interviewId}</dd>
          </dl>
        </div>
      </div>

      {savedResponseQuery.isLoading ? (
        <div className="flex min-h-40 items-center justify-center gap-2 px-4 py-8 text-sm font-bold text-zinc-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          回答を確認しています
        </div>
      ) : savedResponseQuery.isError ? (
        <div className="m-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 sm:m-6">
          保存済み回答を確認できませんでした。画面を再読み込みしてください。
        </div>
      ) : (
        <div>
          <div className="border-b border-zinc-200 px-4 py-5 sm:px-6">
            <h3 className="text-base font-bold text-zinc-950">基本情報</h3>
            <div className="mt-4 grid gap-5 md:grid-cols-2">
              {definition.shortTextItems.map((item) => (
                <label key={item.key} className="space-y-2">
                  <span className="text-sm font-bold text-zinc-800">
                    {item.label}
                    {item.required && <span className="ml-1 text-red-700">*</span>}
                  </span>
                  <input
                    type="text"
                    value={answers.shortText[item.key] ?? ""}
                    placeholder={item.placeholder}
                    onChange={(event) => {
                      const value = event.target.value;
                      setAnswers((current) => ({
                        ...current,
                        shortText: { ...current.shortText, [item.key]: value },
                      }));
                    }}
                    className="h-11 w-full border border-zinc-300 bg-white px-3 text-base text-zinc-950 outline-none focus:border-cyan-700 focus:ring-2 focus:ring-cyan-100"
                  />
                </label>
              ))}
              {definition.singleChoiceItems.map((item) => (
                <fieldset key={item.key} className="space-y-2">
                  <legend className="text-sm font-bold text-zinc-800">
                    {item.label}
                    {item.required && <span className="ml-1 text-red-700">*</span>}
                  </legend>
                  <div className="grid grid-cols-2 gap-2">
                    {item.options.map((option) => {
                      const active = answers.singleChoice[item.key] === option.value;
                      return (
                        <label
                          key={option.value}
                          className={[
                            "flex min-h-11 cursor-pointer items-center justify-center gap-2 border px-3 text-sm font-bold transition-colors",
                            active
                              ? "border-cyan-700 bg-cyan-50 text-cyan-900"
                              : "border-zinc-300 bg-white text-zinc-800 hover:border-cyan-400",
                          ].join(" ")}
                        >
                          <input
                            type="radio"
                            name={`survey-${interviewId}-${item.key}`}
                            value={option.value}
                            checked={active}
                            onChange={() => {
                              setAnswers((current) => ({
                                ...current,
                                singleChoice: { ...current.singleChoice, [item.key]: option.value },
                              }));
                            }}
                            className="h-4 w-4 accent-cyan-700"
                          />
                          {option.label}
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              ))}
            </div>
          </div>

          <SurveyTextQuestions
            items={definition.textItems.filter((item) => item.position === "beforeLikert")}
            answers={answers}
            setAnswers={setAnswers}
          />

          <div className="border-b border-zinc-200 px-4 py-4 sm:px-6">
            <h3 className="text-base font-bold text-zinc-950">{definition.likertTitle}</h3>
            <p className="mt-2 text-xs leading-5 text-zinc-600">
              1は「{definition.scale.labels[0]}」、5は「{definition.scale.labels[definition.scale.labels.length - 1]}」です。
            </p>
          </div>
          <div className="divide-y divide-zinc-100">
            {definition.likertItems.map((item, index) => (
              <fieldset key={item.key} className="grid gap-4 px-4 py-5 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,420px)] lg:items-center">
                <legend className="contents">
                  <span className="flex min-w-0 gap-3 text-sm font-bold leading-6 text-zinc-900 sm:text-base">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-cyan-50 text-sm text-cyan-800">
                      {index + 1}
                    </span>
                    <span>
                      {item.label}
                      {item.required && <span className="ml-1 text-red-700" aria-label="必須">*</span>}
                    </span>
                  </span>
                </legend>
                <div>
                  <div className="grid grid-cols-5 gap-2">
                    {scaleValues.map((score) => {
                      const active = answers.ratings[item.key] === score;
                      return (
                        <button
                          key={score}
                          type="button"
                          onClick={() => {
                            setAnswers((current) => ({
                              ...current,
                              ratings: { ...current.ratings, [item.key]: score },
                            }));
                          }}
                          className={[
                            "h-11 border text-base font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-700 focus-visible:ring-offset-2",
                            active
                              ? "border-cyan-700 bg-cyan-700 text-white"
                              : "border-zinc-200 bg-white text-zinc-700 hover:border-cyan-400 hover:bg-cyan-50",
                          ].join(" ")}
                          aria-pressed={active}
                          aria-label={`${item.label}: ${score}`}
                        >
                          {score}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 grid grid-cols-5 gap-2 text-center text-[10px] font-medium leading-4 text-zinc-500 sm:text-[11px]">
                    {definition.scale.labels.map((label) => (
                      <span key={label}>{label}</span>
                    ))}
                  </div>
                </div>
              </fieldset>
            ))}
          </div>

          <SurveyTextQuestions
            items={definition.textItems.filter((item) => item.position === "afterLikert")}
            answers={answers}
            setAnswers={setAnswers}
          />

          <div className="flex flex-col gap-3 border-t border-zinc-200 bg-zinc-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="text-sm">
              {missingCount > 0 ? (
                <span className="font-bold text-amber-800">未回答の必須項目が {missingCount} 件あります。</span>
              ) : savedAt ? (
                <span className="inline-flex items-center gap-2 font-bold text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" />
                  回答を保存済みです。内容を変更して再保存できます。
                </span>
              ) : (
                <span className="font-bold text-zinc-700">すべての必須項目に回答しました。</span>
              )}
            </div>
            <Button
              type="button"
              data-ui-state={submitMutation.isPending ? "survey-submitting" : "survey-ready"}
              onClick={() => submitMutation.mutate()}
              disabled={submitMutation.isPending || missingCount > 0}
              className={`min-h-11 px-5 font-bold ${EXPERIMENT_PRIMARY_ACTION_CLASS}`}
            >
              {submitMutation.isPending ? <Loader2 className="animate-spin" /> : <Send />}
              {submitMutation.isPending ? "送信中..." : "アンケートを送信"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function SurveyTextQuestions({
  items,
  answers,
  setAnswers,
}: {
  items: ExperimentSurveyTextItem[];
  answers: ExperimentSurveyAnswers;
  setAnswers: Dispatch<SetStateAction<ExperimentSurveyAnswers>>;
}) {
  if (items.length === 0) return null;

  return (
    <div className="border-b border-zinc-200 px-4 py-5 sm:px-6">
      <div className="space-y-5">
        {items.map((item) => (
          <label key={item.key} className="block space-y-2">
            <span className="block whitespace-pre-line text-base font-bold text-zinc-950">
              {item.label}
              {item.required && <span className="ml-1 text-red-700">*</span>}
            </span>
            <span className="block text-sm leading-6 text-zinc-600">{item.helpText}</span>
            <Textarea
              value={answers.freeText[item.key] ?? ""}
              placeholder={item.placeholder}
              onChange={(event) => {
                const value = event.target.value;
                setAnswers((current) => ({
                  ...current,
                  freeText: { ...current.freeText, [item.key]: value },
                }));
              }}
              className="min-h-24 resize-y text-base"
            />
          </label>
        ))}
      </div>
    </div>
  );
}
